<template>
  <card-dialog
    :title="$t('confirmation')"
    :value="value"
    @input="val => $emit('input', val)"
    role="dialog"
    max-width="500px"
  >
    <div v-if="keeperEvent">
      <v-alert
        v-if="hasDifferentTrapNo"
        type="warning"
        dense
        class="mb-3 body-2"
      >
        {{ $t('merge.different_trap_no') }}
      </v-alert>
      <div v-html="confirmMessage"></div>
    </div>
    <div v-else>
      {{ $t('merge.select_two') }}
    </div>
    <template #actions>
      <dialog-action @click="$emit('input', false)">{{ $t('no') }}</dialog-action>
      <dialog-action @click="merge" :disabled="!keeperEvent || merging">{{ $t('yes') }}</dialog-action>
    </template>
  </card-dialog>
</template>

<i18n lang="yaml">
ja:
  confirmation: 確認
  no: いいえ
  yes: はい
  merge:
    confirm: |
      <strong>以下の2つのデータを合算します。</strong><br><br>
      <strong>残すデータ（設置日が早い方）:</strong><br>
      トラップNo: {keeperTrapNo} / 設置日: {keeperInstalledAt} / 回収日: {keeperCollectedAt}<br><br>
      <strong>削除されるデータ:</strong><br>
      トラップNo: {removedTrapNo} / 設置日: {removedInstalledAt} / 回収日: {removedCollectedAt}<br><br>
      <strong>合算後:</strong><br>
      トラップNo: {mergedTrapNo} / 設置日: {mergedInstalledAt} / 回収日: {mergedCollectedAt}<br><br>
      <span style="color: red;">削除されるデータは復元できません。</span>
    success: '合算しました'
    failed: '合算に失敗しました'
    too_many_photos: '写真枚数が多いため合算できません。管理者にご連絡ください。'
    select_two: '合算するデータを2つ選択してください'
    different_trap_no: 'トラップNoが異なります。合算してよいか確認してください。'

en:
  confirmation: Confirmation
  no: No
  yes: Yes
  merge:
    confirm: |
      <strong>The following two data will be merged.</strong><br><br>
      <strong>Data to keep (earlier installation date):</strong><br>
      Trap No: {keeperTrapNo} / Installed: {keeperInstalledAt} / Collected: {keeperCollectedAt}<br><br>
      <strong>Data to be deleted:</strong><br>
      Trap No: {removedTrapNo} / Installed: {removedInstalledAt} / Collected: {removedCollectedAt}<br><br>
      <strong>After merge:</strong><br>
      Trap No: {mergedTrapNo} / Installed: {mergedInstalledAt} / Collected: {mergedCollectedAt}<br><br>
      <span style="color: red;">The deleted data cannot be restored.</span>
    success: 'Merged successfully'
    failed: 'Failed to merge'
    too_many_photos: 'Cannot merge because there are too many photos. Please contact your administrator.'
    select_two: 'Please select exactly 2 data to merge'
    different_trap_no: 'Trap numbers are different. Please confirm you want to merge.'
</i18n>

<script>
import CardDialog from '@/components/atoms/CardDialog';
import DialogAction from '@/components/atoms/DialogAction';
import Notifications from '@/mixins/notifications';

export default {
  name: 'merge-events-dialog',
  components: {
    CardDialog,
    DialogAction,
  },
  mixins: [Notifications],
  inject: ['api'],
  data() {
    return {
      merging: false,
    };
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    selectedEvents: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    keeperEvent() {
      if (this.selectedEvents.length !== 2) return null;
      const [a, b] = this.selectedEvents;
      return a.installed_at <= b.installed_at ? a : b;
    },
    removedEvent() {
      if (this.selectedEvents.length !== 2) return null;
      const [a, b] = this.selectedEvents;
      return a.installed_at <= b.installed_at ? b : a;
    },
    confirmMessage() {
      if (!this.keeperEvent) return '';
      const mergedCollectedAt = this.keeperEvent.collected_at > this.removedEvent.collected_at
        ? this.keeperEvent.collected_at : this.removedEvent.collected_at;
      return this.$t('merge.confirm', {
        keeperTrapNo: this._.escape(this.keeperEvent.trap_no),
        keeperInstalledAt: this._.escape(this.keeperEvent.installed_at),
        keeperCollectedAt: this._.escape(this.keeperEvent.collected_at),
        removedTrapNo: this._.escape(this.removedEvent.trap_no),
        removedInstalledAt: this._.escape(this.removedEvent.installed_at),
        removedCollectedAt: this._.escape(this.removedEvent.collected_at),
        mergedTrapNo: this._.escape(this.keeperEvent.trap_no),
        mergedInstalledAt: this._.escape(this.keeperEvent.installed_at),
        mergedCollectedAt: this._.escape(mergedCollectedAt),
      });
    },
    hasDifferentTrapNo() {
      return this.keeperEvent && this.keeperEvent.trap_no !== this.removedEvent.trap_no;
    },
  },
  watch: {
    value(newValue) {
      if (newValue && this.selectedEvents.length !== 2) {
        this.notifyError('merge.select_two');
        this.$emit('input', false);
      }
    },
  },
  methods: {
    async merge() {
      this.merging = true;
      try {
        const mergedEvent = await this.api.mergeStationPlaceEvents(this.keeperEvent.id, this.removedEvent.id);
        this.$emit('merged', { mergedEvent, deletedEventId: this.removedEvent.id });
        this.notifySuccess('merge.success');
      } catch (error) {
        if (this._.get(error, 'response.data.type') === 'too_many_photos') {
          this.notifyError('merge.too_many_photos');
        } else {
          this.notifyError('merge.failed');
        }
      } finally {
        this.merging = false;
        this.$emit('input', false);
      }
    },
  },
};
</script>
